<template>
  <div ref="svg"></div>
</template>

<script>
export default {
  name: 'ShaftImage',
  props: [
    'shaft',
    'interactive',
    'angle',
    'circleSize',
    'showLabels',
    'showDegrees',
    'width',
    'height',
    'ignoreLabelRotation',
  ],
  data: () => ({
    canvas: undefined,
    angleInputCircle: undefined,
    angleInputLine: undefined,
    currentAngle: 0,
    vw: 1380,
    vh: 1380,
    cx: 690,
    cy: 740,
    dnScaleFactor: 1.0,
  }),
  watch: {
    shaft: {
      handler() {
        this.redraw();
      },
      deep: true,
    },
    angle() {
      if (!this.interactive) return;
      this.currentAngle = this.angle - 90;
      this.updateAngle(this.circleSize, this.cx, this.cy);
    },
  },
  mounted() {
    this.$nextTick(function() {
      if (this.shaft) this.redraw();
    });
  },
  methods: {
    async redraw() {
      if (this.shaft) {
        var svg = await this.openReferenceImage();
        this.createDraw(svg);
        this.colorShaft();

        // calculate scale factor for dn
        if (this.shaft.type) {
          this.dnScaleFactor = this.shaft.type.dn / 1000.0;
        }

        if (this.shaft.side_channels) {
          this.shaft.side_channels.forEach(sideChannel => {
            if (sideChannel.diameter && sideChannel.angle !== undefined)
              this.drawChannel(
                parseInt(sideChannel.diameter.dn),
                360 - parseInt(sideChannel.angle),
                '#666666',
              );
          });
        }
        if (
          this.shaft.output_channel &&
          this.shaft.output_channel.diameter &&
          this.shaft.output_channel.diameter.dn > 0 &&
          this.shaft.output_channel.angle !== undefined
        )
          this.drawChannel(
            parseInt(this.shaft.output_channel.diameter.dn),
            180 + parseInt(this.shaft.output_channel.angle),
            '#000',
          );
        if (
          this.shaft.input_channel &&
          this.shaft.input_channel.diameter &&
          this.shaft.input_channel.diameter.dn > 0
        )
          this.drawChannel(
            parseInt(this.shaft.input_channel.diameter.dn),
            0,
            '#000',
          );

        if (this.interactive) {
          this.drawAngleInput();
          this.currentAngle = this.angle - 90;
          this.updateAngle(this.circleSize, this.cx, this.cy);
        }

        if (this.shaft.side_channels) {
          this.shaft.side_channels.forEach(sideChannel => {
            if (sideChannel.diameter && sideChannel.angle !== undefined)
              this.drawChannelLabel(
                parseInt(sideChannel.diameter.dn),
                parseInt(sideChannel.angle),
                360 - parseInt(sideChannel.angle),
              );
          });
        }
        if (
          this.shaft.output_channel &&
          this.shaft.output_channel.diameter &&
          this.shaft.output_channel.diameter.dn > 0 &&
          this.shaft.output_channel.angle !== undefined
        )
          this.drawChannelLabel(
            parseInt(this.shaft.output_channel.diameter.dn),
            parseInt(this.shaft.output_channel.angle),
            180 + parseInt(this.shaft.output_channel.angle),
          );
        if (
          this.shaft.input_channel &&
          this.shaft.input_channel.diameter &&
          this.shaft.input_channel.diameter.dn > 0
        )
          this.drawChannelLabel(
            parseInt(this.shaft.input_channel.diameter.dn),
            0,
            0,
          );
      }
    },
    openReferenceImage() {
      return new Promise(function(resolve, reject) {
        var xhr = new XMLHttpRequest();
        xhr.open('get', require('@/assets/images/shaft_base.svg'), true);
        xhr.onreadystatechange = function() {
          if (xhr.readyState !== 4) return;
          var status = xhr.status;
          if (status === 200) {
            var svg = xhr.responseXML.documentElement;
            svg = document.importNode(svg, true); // surprisingly optional in these browsers
            resolve(svg);
          } else {
            reject(status);
          }
        };
        xhr.send();
      });
    },
    createDraw(svg) {
      if (this.canvas) this.$refs.svg.removeChild(this.$refs.svg.firstChild);
      this.canvas = this.$svg(svg)
        .addTo(this.$refs.svg)
        .attr('height', null)
        .attr('width', '100%')
        .attr('viewBox', `0 0 ${this.vw} ${this.vh}`)
        .attr('preserveAspectRatio', 'xMinYMin slice');
      if (this.width) this.canvas.attr('width', this.width);
      if (this.height) this.canvas.attr('height', this.height);
      if (!this.showLabels) {
        this.canvas.findOne('#Lables').remove();
      }
      if (!this.showDegrees) {
        this.canvas.findOne('#Degree').remove();
      }
    },
    colorShaft() {
      this.canvas.findOne('#Circle1').attr('style', 'fill:#eeeeee');
      if (
        this.shaft.subtype &&
        (this.shaft.subtype === 1 || this.shaft.subtype.id === 1)
      )
        this.canvas.findOne('#Circle2').attr('style', 'fill:#ffdfbf');
      else if (
        this.shaft.subtype &&
        (this.shaft.subtype === 2 || this.shaft.subtype.id === 2)
      )
        this.canvas.findOne('#Circle2').attr('style', 'fill:#bfdfff');
      else this.canvas.findOne('#Circle2').attr('style', 'fill:#eeeeee');
    },
    drawChannel(dimension, angle, color) {
      var dn = dimension * this.dnScaleFactor;
      var clippingCirle = this.canvas
        .circle(1000)
        .translate(-500 + dn / 2, -500);
      var elem = this.canvas
        .rect(dimension * this.dnScaleFactor, 500)
        .attr({ fill: color });
      elem.clipWith(this.canvas.clip().add(clippingCirle));
      elem.rotate(angle, dn / 2, 0);
      elem.translate(this.cx - dn / 2, this.cy);
      elem = this.canvas.circle(dn).attr({ fill: color });
      elem.translate(this.cx - dn / 2, this.cy - dn / 2);
      return elem;
    },
    drawChannelLabel(dimension, angle, rotationAngle) {
      // draw label
      if (this.showLabels) {
        rotationAngle = ((rotationAngle + 270) % 360) - 180;
        var theta = rotationAngle * (Math.PI / 180);
        const cx = Math.cos(theta) * 480;
        const cy = Math.sin(theta) * 480;

        var text = this.canvas
          .text('DN' + dimension + (angle && angle !== 0 ? `, ${angle}°` : ''))
          .fill('white')
          .attr(
            'style',
            'font-family:"Source Sans Pro", sans-serif; font-size:50px; font-weight:bold',
          )
          .addClass('no-pointer')
          .transform({
            origin: 'right center',
            rotate: rotationAngle,
            translate: { x: cx, y: cy },
            position: [this.cx, this.cy],
          })
          .rotate(angle < 0 ? 180 : 0);

        if (this.ignoreLabelRotation) {
          text.transform({
            origin: 'left center',
            rotate: rotationAngle,
            translate: { x: cx, y: cy },
            position: [this.cx, this.cy],
            flip: 'both',
          });
        }
      }
    },
    drawAngleInput() {
      this.angleInputCircle = this.canvas.circle(50).attr({ fill: 'red' });
      this.angleInputCircle.transform({
        origin: 'center center',
        position: [250 + 25, 25],
      });

      this.angleInputLine = this.canvas.line(this.cx, this.cy, 250 + 25, 25);

      var interacting = false;

      this.canvas.mousedown(
        function() {
          interacting = true;
        }.bind(this),
      );

      this.canvas.mousemove(
        function(e) {
          if (interacting) {
            this.updateAngleFromPointer(
              e.clientX,
              e.clientY,
              this.circleSize,
              this.cx,
              this.cy,
            );
            e.stopPropagation();
            e.preventDefault();
          }
        }.bind(this),
      );

      this.canvas.mouseup(
        function(e) {
          if (interacting) {
            this.updateAngleFromPointer(
              e.clientX,
              e.clientY,
              this.circleSize,
              this.cx,
              this.cy,
            );
            e.stopPropagation();
            e.preventDefault();
          }
          interacting = false;
        }.bind(this),
      );

      this.canvas.touchmove(
        function(e) {
          this.updateAngleFromPointer(
            e.touches[0].clientX,
            e.touches[0].clientY,
            this.circleSize,
            this.cx,
            this.cy,
          );
          e.stopPropagation();
          e.preventDefault();
        }.bind(this),
      );
    },
    updateAngleFromPointer(x, y, circleWidth, centerX, centerY) {
      var point = this.$refs.svg.firstChild.createSVGPoint();
      point.x = x;
      point.y = y;
      var ctm = this.$refs.svg.firstChild.getScreenCTM();
      var inverse = ctm.inverse();
      var p = point.matrixTransform(inverse);

      const theta = Math.atan2(p.y - centerX, p.x - centerY);
      this.currentAngle = theta * (180 / Math.PI);
      this.updateAngle(circleWidth, centerX, centerY);
    },
    updateAngle(circleWidth, centerX, centerY) {
      var theta = this.currentAngle * (Math.PI / 180);

      var angle = theta * (180 / Math.PI);
      angle = ((angle + 270) % 360) - 180;
      this.$emit('angleChanged', angle);

      const cx = Math.cos(theta) * circleWidth;
      const cy = Math.sin(theta) * circleWidth;

      if (this.angleInputCircle)
        this.angleInputCircle.transform({
          origin: 'center center',
          position: [cx + centerX, cy + centerY],
        });
      if (this.angleInputLine) {
        this.angleInputLine.remove();
        this.angleInputLine = this.canvas
          .line(centerX, centerY, cx + centerX, cy + centerY)
          .stroke({ color: 'red', width: 10, linecap: 'round' });
      }
    },
  },
};
</script>

<style>
.no-pointer {
  pointer-events: none;
}
</style>
